import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService, UserRoles } from '../../../../services/auth.service';
import { MCP_MAX_TOOLS_PER_AGENT, McpConnector, McpService } from '../../../../services/mcp.service';
import { Tutor, TutorService, TutorSettingItem } from '../../../../services/tutor.service';

interface ConnectorRow {
  connector: McpConnector;
  assigned: boolean;
  enabledTools: number;
}

/**
 * Agent › MCP-Connectoren: the agent's switch (`agent_mcp_enabled`) and which
 * of the client's connectors it may use. Assigning is admin/manager only —
 * it gives an agent a paid capability of a third party; everyone else sees
 * what is assigned. Connectors themselves are managed on the client page.
 */
@Component({
  selector: 'app-tutor-mcp',
  templateUrl: './tutor-mcp.component.html',
  styleUrl: './tutor-mcp.component.scss',
})
export class TutorMcpComponent implements OnInit, OnDestroy {
  tutorId = '';
  tutor?: Tutor;
  enabledSetting?: TutorSettingItem;
  rows: ConnectorRow[] = [];
  loading = false;
  saving = false;
  readonly maxTools = MCP_MAX_TOOLS_PER_AGENT;

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private mcp: McpService,
    private tutorService: TutorService,
    private auth: AuthService,
    private toastr: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.tutorId =
      this.route.snapshot.paramMap.get('tutorId') ?? this.route.parent?.snapshot.paramMap.get('tutorId') ?? '';
    this.subs.push(
      this.tutorService.tutors$.subscribe(() => (this.tutor = this.tutorService.findTutor(this.tutorId))),
      this.tutorService.tutorSettings$.subscribe((settings) => {
        this.enabledSetting = (settings || []).find((s) => s.variable_name === 'agent_mcp_enabled');
      })
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get canAssign(): boolean {
    const role = this.auth.accessTokenPayload$.value?.role;
    return role === UserRoles.Admin || role === UserRoles.Manager || role === UserRoles.SuperAdmin;
  }

  get featureEnabled(): boolean {
    const v = this.enabledSetting?.value ?? this.enabledSetting?.default_value;
    return v === true || v === 'true';
  }

  get assignedToolCount(): number {
    return this.rows.filter((r) => r.assigned).reduce((n, r) => n + r.enabledTools, 0);
  }

  load(): void {
    this.loading = true;
    // Admins see every connector of the client to choose from; others only
    // what this agent has.
    const all$ = this.canAssign ? this.mcp.list() : this.mcp.listForBot(this.tutorId);
    all$.subscribe({
      next: (connectors) => {
        this.rows = connectors.map((c) => ({
          connector: c,
          assigned: c.assigned_bot_ids.includes(this.tutorId),
          enabledTools: c.tools.filter((tool) => tool.is_enabled && !tool.removed).length,
        }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(this.t.instant('pages.mcp.errors.loadFailed'), '');
      },
    });
  }

  toggleAssigned(row: ConnectorRow, assigned: boolean): void {
    const previous = row.assigned;
    row.assigned = assigned; // mirror first (CLAUDE.md rule 17)
    const ids = this.rows.filter((r) => r.assigned).map((r) => r.connector.id);
    this.saving = true;
    this.mcp.assignToBot(this.tutorId, ids).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(this.t.instant('pages.mcp.agent.saved'), '');
      },
      error: (err) => {
        this.saving = false;
        row.assigned = previous;
        const detail = err?.error?.detail;
        this.toastr.danger(typeof detail === 'string' ? detail : this.t.instant('pages.mcp.errors.saveFailed'), '');
        this.load();
      },
    });
  }

  trackRow = (_: number, row: ConnectorRow) => row.connector.id;
}
